import React, { useEffect } from 'react';
import { Autocomplete, AutocompleteRenderInputParams, Box, Checkbox } from '@mui/material';
import { SearchTextField } from 'src/components/BeaconV2Components/TableSearch';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import colors from 'src/utils/colors';
import FilterChip from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/components/ui/FilterChip';
import { FilterOperatorSchema } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/schema/AlertBuilderSchema';
import {
  BeaconFilterFieldTypeType,
  FilterOperatorType
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/schema/AlertBuilderSchemaTypes';
import { useFormContext, FieldValues, Path, FieldErrors, useFormState, Controller } from 'react-hook-form';
import DynamicBrandFilter from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/components/ui/DynamicBrandFilter';
import { getAppName } from 'src/utils/app';
import { AppName } from 'sl-api-connector';

export type OptionObject = { id: string; name: string };
export type OptionType = string | OptionObject;
// Internal component props for the actual autocomplete UI
type AutocompleteUIProps = {
  idBase: string;
  operator: FilterOperatorType;
  options: Array<OptionType>;
  value: string[];
  freeSolo?: boolean;
  placeholder?: string;
  hasError?: boolean;
  onChange: (labels: string[]) => void;
};

// Props for both single value and array values autocomplete
type FilterValuesAutocompleteProps<T extends FieldValues = FieldValues> = {
  idBase: string;
  options: Array<OptionType>;
  freeSolo?: boolean;
  placeholder?: string;
  showError?: boolean;
  name: Path<T>; // Must end with .values (array field)
  buildItem?: (label: string) => OptionObject;
  fieldType: BeaconFilterFieldTypeType;
};

const DEFAULT_LETTERS_LIMIT = 40;
const DEFAULT_MAX_PILLS = 4;

export const getLabel = (item: OptionType): string => {
  if (typeof item === 'string') return item;
  return String((item as OptionObject).name ?? '');
};

export const toStringArray = (input: OptionType[]): string[] => {
  if (Array.isArray(input)) return input.map(getLabel).filter(Boolean);
  if (typeof input === 'string' && input) return [input];
  return [];
};

// Convert form value (array of objects) to UI value (string array)
export const formValueToUIValue = (formValue: OptionType[]): string[] => {
  if (!formValue) return [];
  if (Array.isArray(formValue)) {
    return formValue.map((item) => getLabel(item));
  }
  return [];
};

// Convert UI value (string array) to form value (array of objects)
export const uiValueToFormValue = (uiValue: string[], buildItem?: (label: string) => OptionObject): OptionType[] => {
  if (!buildItem) {
    return uiValue;
  }

  return uiValue.map((label) => buildItem(label));
};

// Reusable Autocomplete styling
export const AUTOCOMPLETE_STYLES = {
  '& .MuiInputBase-root': {
    minHeight: '40px',
    padding: '4px 12px',
    backgroundColor: 'transparent',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center'
  },
  '& .MuiAutocomplete-inputRoot': {
    alignItems: 'center',
    minHeight: '40px'
  },
  '& .MuiAutocomplete-endAdornment': {
    top: 0,
    right: 8,
    height: '100%',
    display: 'flex',
    alignItems: 'center'
  },
  '& .MuiInputBase-input': {
    padding: 0,
    fontSize: '14px',
    minHeight: '24px',
    lineHeight: '24px'
  },
  '& .MuiAutocomplete-popupIndicator': {
    padding: '4px',
    height: '40px',
    display: 'flex',
    alignItems: 'center'
  },
  '& .MuiAutocomplete-tag': {
    alignSelf: 'center'
  }
};

// Reusable container box styling
export const getContainerBoxStyles = (hasError: boolean) => ({
  border: `2px solid ${hasError ? colors.errorRed : colors.borderGrey}`,
  borderRadius: '4px',
  backgroundColor: 'white',
  minHeight: '40px',
  '&:focus-within': {
    borderColor: hasError ? colors.errorRed : colors.darkerGrey
  }
});

// Reusable FilterTags component
type FilterTagsProps = {
  selected: OptionType[];
  idBase: string;
  onChange: (labels: string[]) => void;
  operator?: FilterOperatorType;
};

export const FilterTags: React.FC<FilterTagsProps> = ({ selected, idBase, onChange, operator }) => {
  if (operator === FilterOperatorSchema.enum['=']) {
    const label = selected.length > 0 ? getLabel(selected[0]) : '';
    return label ? (
      <FilterChip
        key={`${idBase}-single-${label}`}
        keyProp={`${idBase}-single-${label}`}
        label={label}
        onDelete={() => {
          onChange([]);
        }}
      />
    ) : null;
  }

  // IN operator: limit by letters and count
  let totalCharacters = 0;
  const visibleTags: React.ReactNode[] = [];
  let hiddenCount = 0;

  for (let i = 0; i < selected.length; i++) {
    const option = getLabel(selected[i]);
    const optionLength = option.length;
    if (totalCharacters + optionLength <= DEFAULT_LETTERS_LIMIT && visibleTags.length < DEFAULT_MAX_PILLS) {
      totalCharacters += optionLength;
      visibleTags.push(
        <FilterChip
          key={`${idBase}-${option}-${i}`}
          keyProp={`${idBase}-${option}-${i}`}
          label={option}
          onDelete={() => {
            const updatedSelected = selected.filter((_, index) => index !== i);
            const labels = toStringArray(updatedSelected as OptionType[]);
            onChange(labels);
          }}
        />
      );
    } else {
      hiddenCount = selected.length - i;
      break;
    }
  }

  if (hiddenCount > 0) {
    visibleTags.push(
      <FilterChip
        key={`${idBase}-others`}
        keyProp={`${idBase}-others`}
        label={`+ ${hiddenCount} other${hiddenCount > 1 ? 's' : ''}`}
        backgroundColor={colors.lightestGrey}
      />
    );
  }

  return <>{visibleTags}</>;
};

// Reusable SearchInput component
type SearchInputProps = {
  params: AutocompleteRenderInputParams;
  placeholder?: string;
};

export const SearchInput: React.FC<SearchInputProps> = ({ params, placeholder }) => (
  <SearchTextField
    {...params}
    fullWidth
    size="small"
    placeholder={placeholder}
    sx={{
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none'
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        border: 'none'
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        border: 'none'
      }
    }}
  />
);

/**
 * Checks if a field has validation errors, including parent-level errors
 * @param errors - Form errors object from react-hook-form
 * @param fieldPath - Dot-notation path to the field (e.g., "globalFilters.0.values")
 * @returns true if the field or any parent has validation errors
 */
export const hasFieldError = (errors: FieldErrors, fieldPath: string): boolean => {
  if (!errors || !fieldPath) return false;

  const getErrorAtPath = (errorsObj: FieldErrors, path: string) =>
    path.split('.').reduce((current, key) => current?.[key], errorsObj);

  // Check direct field error first
  const directError = getErrorAtPath(errors, fieldPath);
  if (directError) {
    // Only return true if it's an actual error message, not just a nested object
    if (
      typeof directError === 'string' ||
      (directError && typeof directError === 'object' && 'message' in directError)
    ) {
      return true;
    }
  }

  // Check parent-level errors that might affect this field
  // But be more selective - only check immediate parent for array validation errors
  const pathParts = fieldPath.split('.');
  if (pathParts.length > 1) {
    // Check immediate parent (e.g., for "globalFilters.0.values", check "globalFilters.0")
    const parentPath = pathParts.slice(0, -1).join('.');
    const parentError = getErrorAtPath(errors, parentPath);
    if (
      parentError &&
      (typeof parentError === 'string' || (parentError && typeof parentError === 'object' && 'message' in parentError))
    ) {
      return true;
    }
  }

  return false;
};

// Component for array values selection (IN operator)
export function FilterArrayValuesAutocomplete<T extends FieldValues = FieldValues>({
  idBase,
  options,
  freeSolo = false,
  placeholder,
  showError = true,
  name,
  buildItem,
  fieldType
}: FilterValuesAutocompleteProps<T>) {
  const isAtlas = getAppName() === AppName.Atlas;
  const { control } = useFormContext<T>();
  const { errors } = useFormState({ control });
  const hasError = showError ? hasFieldError(errors, name) : false;

  if (fieldType === 'Brand' && isAtlas) {
    return <DynamicBrandFilter idBase={idBase} placeholder="Search brands..." name={name} buildItem={buildItem} />;
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange } }) => {
        const currentUIValue = formValueToUIValue(value);

        return (
          <FilterValuesAutocompleteUI
            idBase={idBase}
            operator="IN"
            options={options}
            value={currentUIValue}
            freeSolo={freeSolo}
            placeholder={placeholder}
            hasError={hasError}
            onChange={(labels) => onChange(uiValueToFormValue(labels, buildItem))}
          />
        );
      }}
    />
  );
}

// Internal UI component that handles the autocomplete display logic
const FilterValuesAutocompleteUI: React.FC<AutocompleteUIProps> = ({
  idBase,
  operator,
  options,
  value,
  freeSolo = false,
  placeholder,
  hasError = false,
  onChange
}) => {
  // Special options for Select All/Unselect All
  const SPECIAL_OPTIONS = {
    SELECT_ALL: '__SELECT_ALL__',
    UNSELECT_ALL: '__UNSELECT_ALL__'
  };

  // Create enhanced options with Select All/Unselect All at the top
  const enhancedOptions = React.useMemo(() => {
    if (operator !== 'IN' || options.length === 0) {
      return options;
    }

    return [SPECIAL_OPTIONS.SELECT_ALL, SPECIAL_OPTIONS.UNSELECT_ALL, ...options];
  }, [options, operator, SPECIAL_OPTIONS.SELECT_ALL, SPECIAL_OPTIONS.UNSELECT_ALL]);

  // Filter out special options from the displayed value
  const displayValue = React.useMemo(() => {
    return value.filter((v) => v !== SPECIAL_OPTIONS.SELECT_ALL && v !== SPECIAL_OPTIONS.UNSELECT_ALL);
  }, [value, SPECIAL_OPTIONS.SELECT_ALL, SPECIAL_OPTIONS.UNSELECT_ALL]);

  const handleChange = (_, newValue: OptionType[]) => {
    const labels = toStringArray(newValue);

    // Handle Select All
    if (labels.includes(SPECIAL_OPTIONS.SELECT_ALL)) {
      const allOptions = toStringArray(options);
      onChange(allOptions);
      return;
    }

    // Handle Unselect All
    if (labels.includes(SPECIAL_OPTIONS.UNSELECT_ALL)) {
      onChange([]);
      return;
    }

    // Regular selection - filter out any special options that might have slipped through
    const filteredLabels = labels.filter(
      (label) => label !== SPECIAL_OPTIONS.SELECT_ALL && label !== SPECIAL_OPTIONS.UNSELECT_ALL
    );

    onChange(filteredLabels);
  };

  return (
    <Box sx={{ flex: 1, minWidth: '640px', marginRight: '-4px' }}>
      {/* No label spacer: keep vertical alignment with adjacent dropdowns */}
      <Box sx={getContainerBoxStyles(hasError)}>
        <Autocomplete
          options={enhancedOptions}
          freeSolo={freeSolo}
          openOnFocus
          forcePopupIcon={false}
          multiple
          disableCloseOnSelect
          disableClearable
          key={`${idBase}-${operator}`}
          value={displayValue}
          onChange={handleChange}
          getOptionLabel={(option) => {
            if (option === SPECIAL_OPTIONS.SELECT_ALL) return 'Select All';
            if (option === SPECIAL_OPTIONS.UNSELECT_ALL) return 'Unselect All';
            return getLabel(option);
          }}
          isOptionEqualToValue={(option, value) => {
            // Handle special options
            if (option === SPECIAL_OPTIONS.SELECT_ALL || option === SPECIAL_OPTIONS.UNSELECT_ALL) {
              return option === value;
            }

            // Support comparing option objects with string values when freeSolo or when value is labels
            const optionLabel = getLabel(option);
            const valueLabel = getLabel(value as OptionType);
            // If both have ids, prefer id equality for stability
            const optionId = typeof option === 'object' ? String((option as OptionObject)?.id) : null;
            const valueId = typeof value === 'object' ? String((value as OptionObject)?.id) : null;
            if (optionId && valueId) return optionId === valueId;
            return optionLabel === valueLabel;
          }}
          renderInput={(params) => <SearchInput params={params} placeholder={placeholder} />}
          renderOption={(props, option, { selected, index }) => {
            const isSpecialOption = option === SPECIAL_OPTIONS.SELECT_ALL || option === SPECIAL_OPTIONS.UNSELECT_ALL;

            const optionId =
              typeof option === 'object' && option !== null && 'id' in (option as OptionObject)
                ? String((option as OptionObject).id)
                : undefined;
            const label = getLabel(option);

            // Handle special option labels
            const displayLabel =
              option === SPECIAL_OPTIONS.SELECT_ALL
                ? 'Select All'
                : option === SPECIAL_OPTIONS.UNSELECT_ALL
                ? 'Unselect All'
                : label;

            // Ensure unique, stable keys: prefer id when available, fallback to index or special option name
            const key = isSpecialOption
              ? `${idBase}-${option}`
              : optionId
              ? `${idBase}-opt-${optionId}`
              : `${idBase}-opt-idx-${index}`;

            return (
              <Box
                component="li"
                {...props}
                key={key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  width: '100%',
                  ...(isSpecialOption && {
                    backgroundColor: '#f5f5f5',
                    borderBottom: index === 1 ? '1px solid #e0e0e0' : 'none', // Add separator after "Unselect All"
                    fontWeight: 500
                  })
                }}
              >
                {isSpecialOption ? (
                  // Special styling for Select All/Unselect All
                  <Box sx={{ padding: '4px 8px', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Text
                      variant="body2"
                      sx={{
                        color: colors.darkBlue,
                        fontWeight: 500,
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {displayLabel}
                    </Text>
                  </Box>
                ) : (
                  <>
                    <Checkbox checked={selected} sx={{ padding: '4px', '&.Mui-checked': { color: colors.darkBlue } }} />
                    <Text variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {displayLabel}
                    </Text>
                  </>
                )}
              </Box>
            );
          }}
          sx={AUTOCOMPLETE_STYLES}
          renderTags={(selected) => (
            <FilterTags selected={selected} idBase={idBase} onChange={onChange} operator={operator} />
          )}
        />
      </Box>
    </Box>
  );
};
